/**
 * TeamStaffManager - Component to manage staff members (coaches, assistants, managers) on a team
 */
import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Users,
  UserPlus,
  Trash2,
  Loader2,
  GraduationCap,
  Shield,
  ClipboardList,
  Award,
  User,
  Mail,
  Send,
  CheckCircle,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Role configurations with icons and colors
const ROLE_CONFIG = {
  head_coach: { label: "Head Coach", icon: GraduationCap, color: "bg-blue-100 text-blue-700" },
  assistant_coach: { label: "Assistant Coach", icon: Users, color: "bg-green-100 text-green-700" },
  team_manager: { label: "Team Manager", icon: ClipboardList, color: "bg-purple-100 text-purple-700" },
  director: { label: "Director", icon: Shield, color: "bg-amber-100 text-amber-700" },
  coordinator: { label: "Coordinator", icon: Award, color: "bg-cyan-100 text-cyan-700" },
  trainer: { label: "Trainer", icon: User, color: "bg-rose-100 text-rose-700" },
};

const TeamStaffManager = ({
  team,
  token,
  onStaffUpdated,
  isOpen,
  onClose,
}) => {
  const [staff, setStaff] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  
  // Add staff dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedRole, setSelectedRole] = useState("assistant_coach");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);

  const requestEpoch = useRef(0);

  // Fetch team staff when dialog opens
  useEffect(() => {
    requestEpoch.current += 1;
    setStaff([]);
    setAvailableStaff([]);
    setSelectedStaffId("");
    setSelectedRole("assistant_coach");
    setAdding(false);
    setRemoving(null);
    setResendingInvite(null);
    setShowAddDialog(false);
    const teamId = team?.team_id || team?.id || team?._id;
    if (isOpen && teamId) {
      fetchTeamStaff();
      fetchAvailableStaff();
    }
    return () => { requestEpoch.current += 1; };
  }, [isOpen, team?.team_id, team?.id, team?._id, token]);

  const getTeamId = () => team?.team_id || team?.id || team?._id;

  const fetchTeamStaff = async () => {
    const epoch = requestEpoch.current;
    const teamId = getTeamId();
    if (!teamId) {
      console.error("No team_id found in team object:", team);
      toast.error("Failed to load team staff - invalid team data");
      return;
    }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/teams/${teamId}/staff`, { headers });
      if (epoch !== requestEpoch.current) return;
      setStaff(response.data.staff || []);
      setAvailableRoles(response.data.available_roles || []);
    } catch (error) {
      if (epoch !== requestEpoch.current) return;
      setStaff([]);
      console.error("Error fetching team staff:", error);
      toast.error("Failed to load team staff");
    } finally {
      if (epoch === requestEpoch.current) setLoading(false);
    }
  };

  const fetchAvailableStaff = async () => {
    const epoch = requestEpoch.current;
    setLoadingCandidates(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/teams/${getTeamId()}/available-staff`, { headers });
      if (epoch !== requestEpoch.current) return;
      setAvailableStaff(response.data.staff || []);
    } catch (error) {
      if (epoch !== requestEpoch.current) return;
      setAvailableStaff([]);
      toast.error("Failed to load eligible staff. Close and reopen to retry.");
    } finally {
      if (epoch === requestEpoch.current) setLoadingCandidates(false);
    }
  };

  const handleAddStaff = async () => {
    const epoch = requestEpoch.current;
    if (!selectedStaffId || !selectedRole) {
      toast.error("Please select a staff member and role");
      return;
    }

    const teamId = getTeamId();
    if (!teamId) {
      toast.error("Invalid team data");
      return;
    }

    const selectedPerson = availableStaff.find(person => JSON.stringify([person.staff_type, person.staff_id]) === selectedStaffId);
    if (!selectedPerson) {
      toast.error("Select an eligible staff member");
      return;
    }

    setAdding(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/teams/${teamId}/staff`,
        {
          staff_id: selectedPerson.staff_id,
          staff_type: selectedPerson.staff_type,
          role: selectedRole,
        },
        { headers }
      );
      if (epoch !== requestEpoch.current) return;
      toast.success("Staff member added to team");
      setShowAddDialog(false);
      setSelectedStaffId("");
      setSelectedRole("assistant_coach");
      fetchTeamStaff();
      if (onStaffUpdated) onStaffUpdated();
    } catch (error) {
      if (epoch !== requestEpoch.current) return;
      const message = error.response?.data?.detail || "Failed to add staff member";
      toast.error(message);
    } finally {
      if (epoch === requestEpoch.current) setAdding(false);
    }
  };

  const handleRemoveStaff = async (staffId) => {
    const epoch = requestEpoch.current;
    setRemoving(staffId);
    try {
      const teamId = getTeamId();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/teams/${teamId}/staff/${staffId}`, { headers });
      if (epoch !== requestEpoch.current) return;
      toast.success("Staff member removed from team");
      fetchTeamStaff();
      fetchAvailableStaff();
      if (onStaffUpdated) onStaffUpdated();
    } catch (error) {
      if (epoch !== requestEpoch.current) return;
      toast.error("Failed to remove staff member");
    } finally {
      if (epoch === requestEpoch.current) setRemoving(null);
    }
  };

  const handleUpdateRole = async (staffId, newRole) => {
    const epoch = requestEpoch.current;
    try {
      const teamId = getTeamId();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/teams/${teamId}/staff/${staffId}?role=${newRole}`,
        {},
        { headers }
      );
      if (epoch !== requestEpoch.current) return;
      toast.success("Role updated");
      fetchTeamStaff();
      if (onStaffUpdated) onStaffUpdated();
    } catch (error) {
      if (epoch !== requestEpoch.current) return;
      toast.error("Failed to update role");
    }
  };

  const handleResendSquadlineInvite = async (staffId, staffName) => {
    const epoch = requestEpoch.current;
    const teamId = getTeamId();
    if (!teamId) {
      toast.error("Invalid team data");
      return;
    }

    // Check if team is synced to Squadline
    if (!team?.squadline_team_id) {
      toast.error("Team is not synced to Squadline. Please sync the team first.");
      return;
    }

    setResendingInvite(staffId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/squadline/resend-invite/${teamId}/staff/${staffId}`,
        {},
        { headers }
      );
      if (epoch !== requestEpoch.current) return;
      toast.success(`Squadline invite sent to ${response.data.email || staffName}`);
    } catch (error) {
      if (epoch !== requestEpoch.current) return;
      const message = error.response?.data?.detail || "Failed to resend invite";
      toast.error(message);
    } finally {
      if (epoch === requestEpoch.current) setResendingInvite(null);
    }
  };

  // Existing mutation URLs are ID-based, so exclude any already assigned ID.
  const unassignedStaff = availableStaff.filter(
    person => !staff.some(member => member.staff_id === person.staff_id)
  );

  const getRoleConfig = (role) => ROLE_CONFIG[role] || {
    label: availableRoles.find(option => option.value === role)?.label || role || 'Role not specified',
    icon: User, color: 'bg-slate-100 text-slate-700'
  };

  return (
    <>
      {/* Main Staff Management Dialog */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg" data-testid="team-staff-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Manage Team Staff
            </DialogTitle>
            <DialogDescription>
              Assign eligible organization staff and coaches to {team?.name || "this team"}.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Current Staff List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium">Current Staff ({staff.length})</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAddDialog(true)}
                  className="rounded-lg min-h-[44px]"
                  data-testid="add-staff-btn"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  Add Staff
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : staff.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-xl">
                  <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No staff assigned to this team</p>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => setShowAddDialog(true)}
                    className="mt-2"
                  >
                    Add your first staff member
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {staff.map((member) => {
                    const roleConfig = getRoleConfig(member.role);
                    const RoleIcon = roleConfig.icon;
                    return (
                      <div
                        key={member.staff_id}
                        className="flex flex-wrap gap-3 items-center justify-between p-3 bg-slate-50 rounded-xl group hover:bg-slate-100 transition-colors"
                        data-testid={`staff-member-${member.staff_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
                            {member.avatar_url ? (
                              <img
                                src={member.avatar_url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                          <div className="min-w-0 break-words">
                            <p className="font-medium text-slate-900 text-sm">
                              {member.first_name} {member.last_name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge className={`${roleConfig.color} text-xs font-medium`}>
                                <RoleIcon className="w-3 h-3 mr-1" />
                                {roleConfig.label}
                              </Badge>
                              {/* Squadline acceptance status */}
                              {team?.squadline_team_id && (
                                member.squadline_accepted ? (
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Squadline Active
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Pending Invite
                                  </Badge>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Select
                            value={member.role}
                            onValueChange={(value) => handleUpdateRole(member.staff_id, value)}
                          >
                            <SelectTrigger className="w-[140px] min-h-[44px] text-sm" aria-label={`Role for ${member.first_name} ${member.last_name}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableRoles.map((role) => (
                                <SelectItem key={role.value} value={role.value}>
                                  {role.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {/* Resend Squadline Invite Button */}
                          {team?.squadline_team_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-11 w-11 p-0 text-slate-500 hover:text-blue-500 hover:bg-blue-50"
                              onClick={() => handleResendSquadlineInvite(member.staff_id, `${member.first_name} ${member.last_name}`)}
                              disabled={resendingInvite === member.staff_id}
                              title="Resend Squadline invite"
                              data-testid={`resend-invite-${member.staff_id}`}
                            >
                              {resendingInvite === member.staff_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-11 w-11 p-0 text-slate-500 hover:text-red-500 hover:bg-red-50"
                            aria-label={`Remove ${member.first_name} ${member.last_name} from team`}
                            onClick={() => handleRemoveStaff(member.staff_id)}
                            disabled={removing === member.staff_id}
                            data-testid={`remove-staff-${member.staff_id}`}
                          >
                            {removing === member.staff_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} className="rounded-lg">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md" data-testid="add-staff-dialog">
          <DialogHeader>
            <DialogTitle>Add Staff to Team</DialogTitle>
            <DialogDescription>
              Select a staff member and assign their role on {team?.name || "this team"}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Coach Selection */}
            <div>
              <Label className="text-sm mb-2 block">Select Staff</Label>
              {loadingCandidates ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : unassignedStaff.length === 0 ? (
                <div className="text-center py-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700">
                    No eligible staff available to assign.
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Staff must belong to this organization and be eligible for this program.
                  </p>
                </div>
              ) : (
                <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                  <SelectTrigger className="w-full" data-testid="select-staff" aria-label="Select staff member">
                    <SelectValue placeholder="Select a staff member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {unassignedStaff.map((person) => (
                      <SelectItem key={JSON.stringify([person.staff_type, person.staff_id])} value={JSON.stringify([person.staff_type, person.staff_id])}>
                        {person.first_name} {person.last_name} — {person.staff_type === 'coach' ? 'Coach' : 'Staff'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Role Selection */}
            <div>
              <Label className="text-sm mb-2 block">Assign Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-full" data-testid="select-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_CONFIG).map(([value, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={value} value={value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span>{config.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddStaff}
              disabled={adding || !selectedStaffId}
              className="rounded-lg bg-slate-900 hover:bg-slate-800"
              data-testid="confirm-add-staff"
            >
              {adding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add to Team
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeamStaffManager;
